/**
 * Single source of truth for rendering a poster / thumbnail image with a
 * graceful fallback when the URL is missing, broken, blocked by CSP, or
 * the remote server returns 404 / a non-image content type.
 *
 * Two-stage fallback:
 *   1. Try the explicit `src` (typically the provider's `stream_icon` /
 *      `cover` / `logo_url`).
 *   2. On failure, if `tmdbFallback={name, year, kind}` was passed, look
 *      up the title in the server-side community resolver / TMDB cache
 *      and retry with the TMDB poster URL.
 *   3. On final failure, render the Lucide placeholder icon.
 *
 * The TMDB fallback is what fixes the long-standing "Plex / B2C poster
 * shows as a placeholder" gripe — providers that don't return cover
 * URLs (or return broken ones) now seamlessly inherit posters from the
 * community DB.
 */
import React, { useState, useEffect, useRef } from "react";
import { Tv } from "lucide-react";
import { getCachedTmdb, peekCachedTmdb } from "../lib/tmdbCache";
import { useCachedImage } from "../lib/imageCache";

// URL strings we KNOW will fail — short-circuit before even mounting the
// <img>. Saves a wasted network request and prevents the WebView broken-
// image glyph flashing for a frame.
const KNOWN_BAD = new Set([
  "n/a",
  "null",
  "undefined",
  "none",
  "false",
  "0",
]);

function isUsable(src) {
  if (!src || typeof src !== "string") return false;
  const trimmed = src.trim();
  if (!trimmed) return false;
  if (KNOWN_BAD.has(trimmed.toLowerCase())) return false;
  // Anything non-empty and not in the explicit blocklist is worth trying.
  // The <img> onError handler will catch CORS failures, 404s, malformed
  // URLs etc and swap us into the placeholder branch — being too strict
  // here was suppressing perfectly good Xtream poster URLs that happened
  // to use protocol-relative or unusual schemes.
  return true;
}

export default function PosterImage({
  src,
  alt,
  className = "",
  fallbackClassName = "",
  fallbackIcon: FallbackIcon = Tv,
  fallbackIconClassName = "w-10 h-10 text-slate-700",
  tmdbFallback = null, // { name, year, kind: "movie"|"series" }
}) {
  // Parse the cache-proxy URL prefix once for the background-fetch
  // retry logic below. MUST match the runtime backend resolution in
  // imageCache.js — the Capacitor APK talks to zaplayerpro.club, the
  // browser dev preview talks to REACT_APP_BACKEND_URL.
  const _PROD_BACKEND = "https://zaplayerpro.club";
  const BACKEND_BASE = (() => {
    if (typeof window !== "undefined") {
      if (
        window.Capacitor &&
        typeof window.Capacitor.isNativePlatform === "function" &&
        window.Capacitor.isNativePlatform()
      ) return _PROD_BACKEND;
      if (window.electronAPI?.isElectron) return _PROD_BACKEND;
    }
    return (
      (typeof process !== "undefined" &&
        process.env &&
        (process.env.REACT_APP_PROD_BACKEND_URL || process.env.REACT_APP_BACKEND_URL)) ||
      ""
    );
  })();

  // v1.1.3 — Seed initial state from the SYNC TMDB cache so tiles
  // that were already resolved once don't flash a placeholder every
  // time they scroll off/on-screen. Only used when the parent gave
  // us no primary src AND a tmdbFallback name.
  const _initialState = (() => {
    if (isUsable(src)) return { currentSrc: src, stage: "primary" };
    if (tmdbFallback?.name) {
      const kind = tmdbFallback.kind === "series" ? "series" : "movie";
      const cached = peekCachedTmdb(kind, tmdbFallback.name, tmdbFallback.year);
      const url = cached?.poster || cached?.backdrop;
      if (url) return { currentSrc: url, stage: "tmdb" };
    }
    return { currentSrc: src, stage: "primary" };
  })();
  const [currentSrc, setCurrentSrc] = useState(_initialState.currentSrc);
  const [stage, setStage] = useState(_initialState.stage);
  // retryToken is incremented after a proxy 404 to force the
  // <img> element to re-fetch (we mount with key={retryToken}).
  const [retryToken, setRetryToken] = useState(0);
  // Guard against an unmount-during-async-fetch warning when the user
  // scrolls past the tile before TMDB responds.
  const aliveRef = useRef(true);
  // Single-shot retry guard for proxy URL 404s.
  const proxyRetriedRef = useRef(false);
  // Timer handle for the proxy 4-second retry — cleared on unmount /
  // src change to prevent the slow OOM that built up when the user
  // scrolled past hundreds of un-harvested covers (every miss left a
  // dangling setTimeout closure referencing the unmounted component).
  const retryTimerRef = useRef(null);

  useEffect(() => () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // Remember the src prop we last committed so we only re-run the
  // stage-reset when the parent actually gives us a NEW src value.
  // On initial mount `lastSrcRef.current === src` so the effect is a
  // no-op — this is what preserves the TMDB-seeded state from the
  // sync cache lookup above.
  const lastSrcRef = useRef(src);

  useEffect(() => {
    if (lastSrcRef.current === src) return; // same prop → no-op
    lastSrcRef.current = src;
    // Parent gave us a genuinely new primary URL — reset back to stage 1.
    setCurrentSrc(src);
    setStage("primary");
    setRetryToken(0);
    proxyRetriedRef.current = false;
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, [src]);

  // v1.4.27 — TMDB pre-fetch for tiles that have NO primary cover URL.
  //
  // The <img>-onError fallback below only fires when a real URL fails
  // to load. If the parent passed `src={null}` (Xtream series that
  // don't have a `cover` field populated — e.g. many local-language
  // series from certain providers) we used to skip straight to the
  // grey placeholder without ever consulting TMDB. This effect
  // matches vupro.apk's behaviour: whenever we lack a primary
  // poster AND we have a name to search on, do the same TMDB
  // lookup handleError does — but proactively.
  useEffect(() => {
    if (currentSrc) return; // primary is present — nothing to do
    if (stage !== "primary") return; // already fell through
    if (!tmdbFallback?.name) return; // nothing to search on
    let cancelled = false;
    // v1.1.0 — Debounce by 150 ms. On Smart-TV fast-scroll through a
    // 500-series category the WebView mounts and unmounts hundreds
    // of PosterImage instances a second; without the debounce every
    // one of those flashes fires a TMDB request that never gets used
    // (the tile is gone before the response arrives). 150 ms is
    // shorter than a "settle" but long enough to skip a scroll pass.
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const kind = tmdbFallback.kind === "series" ? "series" : "movie";
        const tmdb = await getCachedTmdb(kind, tmdbFallback.name, tmdbFallback.year);
        if (cancelled || !aliveRef.current) return;
        const fallbackUrl = tmdb?.poster || tmdb?.backdrop;
        if (fallbackUrl) {
          setCurrentSrc(fallbackUrl);
          setStage("tmdb");
        }
      } catch {
        /* swallow — placeholder path takes over */
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    currentSrc,
    stage,
    tmdbFallback?.name,
    tmdbFallback?.kind,
    tmdbFallback?.year,
  ]);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Always-defined renderable src — useCachedImage rewrites Plex URLs
  // to our backend cover-proxy form. Called BEFORE handleError so the
  // proxy detection inside handleError can inspect the rewritten URL.
  const renderSrc = useCachedImage(currentSrc);

  function _isCoverProxyUrl(u) {
    return typeof u === "string" && BACKEND_BASE &&
      u.indexOf(`${BACKEND_BASE}/api/covers/`) === 0;
  }

  // Fade-in guard — the <img> stays invisible until `onLoad` fires.
  // This is the fix for the "broken-image glyph flash" the user sees
  // while a dead provider URL is trying to load. If the load fails,
  // opacity stays at 0 and the placeholder underneath shows through.
  // Reset every time the src or retryToken changes.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(false); }, [currentSrc, retryToken, renderSrc]);

  const handleError = async () => {
    // Stage 0.5 — proxy-URL background-fetch retry. When the backend
    // returns 404 for a cover it ALSO queues an upstream Plex fetch;
    // ~4 seconds later the bytes are on disk. Re-mount the <img>
    // with a different key so the WebView actually re-requests.
    // Check renderSrc (the ACTUAL url the img loaded) not currentSrc
    // (which is the raw Plex url before useCachedImage rewriting).
    if (
      stage === "primary" &&
      _isCoverProxyUrl(renderSrc) &&
      !proxyRetriedRef.current
    ) {
      proxyRetriedRef.current = true;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        if (!aliveRef.current) return;
        setRetryToken((n) => n + 1);
      }, 4000);
      return;
    }

    if (stage === "primary" && tmdbFallback?.name) {
      // tmdbCache expects the same `kind` strings the catalog uses
      // ("series" for TV, "movie" for VOD). Don't convert to TMDB-style
      // "tv" here — the inner searchOne() already does that
      // translation when building the URL.
      const kind = tmdbFallback.kind === "series" ? "series" : "movie";
      // v1.1.0 — tmdbCache.getCachedTmdb now has an internal
      // concurrency limiter + persistent negative cache, so calling it
      // straight from onError is safe even for a 500-tile Series
      // category. No need to debounce here (the browser only fires
      // onError for tiles that actually attempted a load).
      try {
        const tmdb = await getCachedTmdb(kind, tmdbFallback.name, tmdbFallback.year);
        if (!aliveRef.current) return;
        // `getCachedTmdb` returns the full TMDB detail block which has
        // `poster` and `backdrop` (already absolute https URLs from our
        // server proxy — no need to prepend image.tmdb.org).
        const fallbackUrl = tmdb?.poster || tmdb?.backdrop;
        if (fallbackUrl) {
          setCurrentSrc(fallbackUrl);
          setStage("tmdb");
          return;
        }
      } catch {
        /* swallow — fall through to placeholder */
      }
    }
    if (!aliveRef.current) return;
    setStage("placeholder");
  };

  // Full-placeholder branch — no src to try, or already fell through.
  if (!isUsable(currentSrc) || stage === "placeholder") {
    return (
      <div
        className={
          "w-full h-full flex items-center justify-center bg-[#0d1018] " +
          fallbackClassName
        }
      >
        <FallbackIcon className={fallbackIconClassName} />
      </div>
    );
  }

  // Stacked render: placeholder underneath, <img> on top with
  // opacity-0 until it successfully loads. Failed loads never
  // reveal the browser's broken-image glyph — the placeholder
  // shows through the still-invisible <img>.
  return (
    <div className={`relative w-full h-full bg-[#0d1018] ${fallbackClassName}`}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <FallbackIcon className={fallbackIconClassName} />
        </div>
      )}
      <img
        key={`${currentSrc}-${retryToken}`}
        src={renderSrc}
        alt={alt}
        loading="lazy"
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
        onError={handleError}
        style={{
          opacity: loaded ? 1 : 0,
          transition: "opacity 180ms ease-out",
        }}
        className={`absolute inset-0 w-full h-full ${className}`}
      />
    </div>
  );
}
